/* The shell every Momentum panel is built from.
 *
 * Spec step 2 — docs/momentum-hierarchy-spec.md 8.1 and 9.2. Every tool in the
 * nav is one of two things, a library or a composed record, and both get the
 * same chrome around them: a bar with Add, the view toggle and (where the tool
 * has one) the Level 1 switcher; a rail of records down the left of the copy
 * view; a footer under the one she is writing. Written once here so that
 * flipping a rule flips every panel at the same moment rather than three times
 * a month apart — which is how Website ended up with arrows while Course and
 * Outreach had nothing, and how the same rail got three different dot
 * placements.
 *
 * NOTHING HERE KNOWS WHAT A PAGE, A MODULE, A SEQUENCE OR AN OFFER IS. The rail
 * is handed a list and a function that says what one row reads; the bar is
 * handed the controls that go in it. Same boundary as ui.jsx and sections.jsx,
 * and the same reason: it is what stops this becoming the next 3,700-line file.
 *
 * WHAT IT DELIBERATELY DOES NOT DO: it does not decide what colour a record's
 * dot is. Three panels currently answer "is this done" three different ways
 * (Website from its sections, Course from `status`, Outreach from its touches)
 * and unifying them is step 5's job, after her decision 6.1. Making the rail
 * pick one now would quietly change what a dot means on two screens in a change
 * that is supposed to be about layout.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Eye, MessageSquare, Plus, Trash2 } from "lucide-react";
import { BrowseHead, BrowseRow, SortableItem, SortableList, browse, useBrowseNarrow } from "../BrowseTable";
import { B, btnGhost, input, nb } from "./tokens";
import { SavedTick, ViewToggle, useTwoPane } from "./ui";


/* ── The bar ────────────────────────────────────────────────────────────────
   One row across the top of both views, so a control never moves when she
   switches between them — a toggle that moves when you use it is a toggle you
   have to hunt for.

   READING ORDER IS LEFT TO RIGHT BY SCOPE, and her words for it (2026-09-15)
   are the rule: "back to the page, my site, homepage, then add page — my site
   comes first in the hierarchy." So: the way back, then the SITE, then the PAGE
   inside it, then Add, then anything that tool alone offers (Pull from My
   Offer). The way of looking at what is already there sits out at the right,
   out of the way of the action.

   That is why `level2` is its own slot rather than something the caller tacks
   onto `lead`. The page picker went in `lead` when the drafting screen first
   gave up its own chrome row, which put it BEFORE the site it belongs to — the
   narrower thing first. A slot in the right place cannot be filled in the wrong
   order.

   `level1` and `level2` are slots rather than flags: Outreach has no Level 1 and
   must not grow a dropdown with one item in it (spec 8.5), and only the drafting
   screen has a Level 2 to name. */
export function MomentumBar({ lead, level1, level2, add, extras, view, setView, copyLabel, drawer, gap = 22, levels = 3, drafting = false }) {
  const twoPane = useTwoPane();
  /* ON A PHONE, WHILE SHE IS DRAFTING, THIS WHOLE ROW GOES. Her report,
     2026-09-22, at 320 and 375: back, the site, the page and Add stacked into
     four rows above a chat that then had four lines of conversation in it and
     its input below the fold — "it should just be one line at the top so you
     can see the chat."

     So the drafting screen takes its own chrome back (drafter.jsx draws one
     row: the way back, where she is, the scope, the panes) and this stands
     down. The drawer still renders: it is the Level 1 settings card, which is
     only open because she opened it.

     AT EVERY WIDTH SINCE 2026-09-24. This used to keep the bar on a wide
     screen, "where the row is one line and the space it costs is free". It is
     not free: her read on a laptop was "the chat window is TINY, all this gunk
     up top is really too much — I don't think we need to be able to add a page
     from here." The phone's one row, at every width. */
  if (drafting) return drawer ? <div style={{ marginBottom: gap }}>{drawer}</div> : null;
  return (
    <div style={{ marginBottom: gap }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          {/* The way BACK sits before the thing it comes back to. Only the
              drafting screen fills this, and filling it is what stops that
              screen growing a second chrome row of its own. */}
          {lead}
          {level1}
          {level2}
          {add}
          {extras}
        </div>
        {/* ONLY WHERE BOTH VIEWS SHOW THE SAME SET. Outreach has no Level 1, so
            its Table and its Copy are two renderings of one list of sequences
            and a toggle is the honest control. On Sites and Courses the Table
            is now the level ABOVE — all sites, all courses — so switching to it
            is leaving the record, not looking at it differently. That is a way
            BACK, and it renders in `lead` as one. A toggle that changes level
            is a different gesture wearing the same clothes, and it is exactly
            why the old one never read as the way out (her words, 2026-09-15:
            "it feels like table is the first page"). */}
        {/* AND NOT ON A PHONE IN A TWO-LEVEL PANEL, where the Table and the
            Copy have collapsed into the same screen (see `levels` on
            RecordCopy). Two buttons that go to the one place she is already
            looking at is not a choice, it is furniture. */}
        {setView && (levels !== 2 || twoPane) && <ViewToggle view={view} setView={setView} copyLabel={copyLabel} />}
      </div>
      {/* The Level 1 settings card, when the cog is open. Under the bar rather
          than floating over it: it holds a name she is typing into, and a menu
          that closes when the page behind it is clicked is the wrong shape for
          that. */}
      {drawer}
    </div>
  );
}


/* THE WAY BACK, as the bar draws it — one button, one shape, six panels.
   Sites, Courses and Presentations each wrote this same `btnGhost` by hand,
   and the five drafting screens about to fill `lead` would have made eight
   copies of a button whose whole job is to look identical wherever it is.

   It takes a LABEL and a handler rather than a node, for the reason `where`
   takes a string: a slot that accepts anything gets filled six ways. */
export function BarBack({ label, onClick }) {
  if (!onClick) return null;
  return (
    <button onClick={onClick} style={{ ...btnGhost, padding: "10px 14px", fontSize: 13 }}>{label}</button>
  );
}


/* ── The top-level table ────────────────────────────────────────────────────
   Every panel's landing screen: all of the biggest thing it holds, in a row
   each, draggable into her order.

   HER CALL, 2026-09-15: "should that table actually be all the sites, because
   when you get into the pages it has the list on the left anyway" — and then
   "all of the sections should mirror this paradigm but of course the table
   columns are different." She is right about the redundancy. On Sites the rail
   already carried four of the six facts the pages table showed; the table was
   adding a last-updated column and a bin.

   So the rule is now: THE TABLE IS THE TOP LEVEL THAT EXISTS. Sites shows
   sites, Courses shows courses, and Outreach — which has no Level 1 by decision
   (spec 8.5) — shows sequences, which IS its top level. That reads as one rule
   rather than as two panels and an exception.

   NOTHING HERE KNOWS WHAT A SITE OR A COURSE IS, same boundary as the rail
   above it. It is handed the records, the column widths, the labels, and a
   `rowFor` that returns the CELLS one record reads. "The table columns are
   different" is the caller's sentence, and this is the shape that lets it be.

   The grip is the first cell, so `lead` is one greater than the caller's own
   lead: the column list it passes describes its own cells and this adds the
   one it does not know about. */
export function L1Table({
  records = [], groups = null, cols, labels = [], rowFor, onOpen, onDelete, onReorder,
  loading, loadingText, empty, deleteTitle = "Delete", lead,
  /* WHAT ONE ROW READS ON A PHONE, when the wide row says too much to stack.
     Her call, 2026-09-18, on the Offer Ladder: a row was a name, a summary, a
     type pill, a price, a status and a destination, and "it doesn't need all
     that detail in mobile". The type pill went because the group heading above
     it already says Trust Building.

     Two cells: what she reads, then what she presses. BrowseRow pins the LAST
     child right, so the second cell is the dot and the chevron together.

     The GRIP stays (716ecd5 — Sites reordered on her phone all along); the
     DELETE does not: it lives inside the record, one tap further from a
     scrolling thumb, and stays on the wide table.

     Optional: a panel that says nothing gets its wide row stacked, which is
     what every other table does today. */
  narrowRowFor,
}) {
  /* Below 640px these rows stop being a grid — see BrowseRow. */
  const narrow = useBrowseNarrow();

  /* GROUPS ARE OPTIONAL, and the Offer Ladder is why they exist: a ladder has
     rungs in an order, so its table is banded by what each offer is FOR rather
     than being one flat list. Every other panel passes `records` and gets one.

     `groups` is [{ key, label, rows, dim }] — the caller's own shape (see
     `groupOffers`), because what a group MEANS is a panel's business. This knows
     only that some rows sit under a heading. */
  const banded = groups || [{ key: "", label: null, rows: records }];
  const all = banded.flatMap((g) => g.rows);

  /* THE GRIP ONLY APPEARS WHERE SOMETHING LISTENS. Offers have a `sort_order`
     column that nothing writes yet, and a grip that picks a row up and puts it
     back is worse than no grip — it reads as broken rather than as absent. */
  const draggable = !!onReorder;
  const leadCells = lead ?? (draggable ? 2 : 1);

  /* Same rule as the Offer Ladder's own table: only the FIRST load blanks it,
     because an empty table reads as "you have none" rather than "still
     loading". */
  if (loading && all.length === 0) {
    return (
      <div style={{ border: `1px dashed ${B.border2}`, borderRadius: 10, padding: 30, textAlign: "center", color: B.dim, fontSize: 13 }}>
        {loadingText || "Loading…"}
      </div>
    );
  }
  if (all.length === 0) {
    return (
      <div style={{ border: `1px dashed ${B.border2}`, borderRadius: 10, padding: 30, textAlign: "center", color: B.dim, fontSize: 13, lineHeight: 1.6 }}>
        {empty}
      </div>
    );
  }

  /* The phone row keeps the GRIP, same as Sites' table and rail on a phone.
     It came off in 7c10ed2 on "a grip between two thumbs is a drag she would
     lose" — my reason, not hers, and Sites had reordered on her phone the
     whole time. Her read, 2026-09-23: "but sites reorganizes in mobile". */
  const row = (r, i, dim, handle, dragging) => (narrow && narrowRowFor ? (
    <BrowseRow cols={cols} narrow lead={draggable ? 2 : 1} onClick={() => onOpen?.(r)}
      style={{ cursor: "pointer", opacity: dragging ? 0.4 : dim ? 0.6 : 1 }}>
      {draggable && <span onClick={(e) => e.stopPropagation()} style={{ display: "flex", flexShrink: 0 }}>{handle}</span>}
      {narrowRowFor(r, i)}
    </BrowseRow>
  ) : (
    <BrowseRow cols={cols} narrow={narrow} lead={leadCells} onClick={() => onOpen?.(r)}
      style={{ cursor: "pointer", opacity: dragging ? 0.4 : dim ? 0.6 : 1 }}>
      {/* stopPropagation, or reaching for the grip opens the record she was only
          trying to move. Same rule as the rail. */}
      {draggable && <span onClick={(e) => e.stopPropagation()} style={{ display: "flex", flexShrink: 0 }}>{handle}</span>}
      {rowFor(r, i)}
      <div onClick={(e) => e.stopPropagation()} style={{ display: "flex" }}>
        {onDelete && (
          <button onClick={() => onDelete(r)} title={deleteTitle}
            style={{ background: "none", border: "none", cursor: "pointer", color: B.faint, padding: 4, display: "flex" }}>
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </BrowseRow>
  ));

  return (
    <div style={browse}>
      <BrowseHead cols={cols} narrow={narrow} labels={draggable ? ["", ...labels] : labels} />
      {banded.map((g) => (
        <div key={g.key || "all"}>
          {g.label && (
            <div style={{ padding: "9px 16px", background: B.surface2, borderBottom: `1px solid ${B.border}`, fontSize: 10.5, letterSpacing: "0.09em", textTransform: "uppercase", fontWeight: 700, color: g.dim ? B.faint : B.label }}>
              {g.label}
            </div>
          )}
          {draggable ? (
            <SortableList ids={g.rows.map((r) => r.id)} onReorder={(from, to) => onReorder(from, to, g.key)}>
              {g.rows.map((r, i) => (
                <SortableItem key={r.id} id={r.id}>
                  {({ handle, dragging }) => row(r, i, g.dim, handle, dragging)}
                </SortableItem>
              ))}
            </SortableList>
          ) : (
            g.rows.map((r, i) => <div key={r.id}>{row(r, i, g.dim)}</div>)
          )}
        </div>
      ))}
    </div>
  );
}


/* ── What a site or a course IS, behind a cog ───────────────────────────────
   Rule 8: settings almost nobody changes live behind a cog, not as form fields
   in the way of the work. A Level 1 has two or three of them — what it is
   called, where it lives — set once and then read.

   It is also the only place a Level 1 can be DELETED, and that delete takes its
   children with it: `website_pages.site_id` and `lesson_plans.course_id` are
   both `on delete cascade`, because a page with no site is a page no screen can
   reach. So the caller passes a ConfirmDialog that says how many, by name. */
export function L1Settings({ noun = "site", record, fields = [], onSave, onDelete, deleteLabel }) {
  /* Holds its own draft and commits on the way OUT of a field, exactly like a
     block does. Writing on every keystroke would be a Supabase round trip per
     character on a name she types once.

     KEYED BY THE RECORD at the call site, and it has to stay keyed: this state
     is initialised once, so re-rendering the same instance against a different
     site would leave the previous site's name in the field — and the blur then
     writes it in, which is the bug the section drafter had (spec 5.4). */
  const [draft, setDraft] = useState(record || {});

  const commit = (k) => {
    const was = record?.[k] ?? "";
    const now = draft?.[k] ?? "";
    if (String(was) === String(now)) return;   // a blur that changed nothing costs nothing
    onSave?.({ ...record, ...draft });
  };

  return (
    <div style={{ border: `1px solid ${B.border}`, borderRadius: 10, background: B.surface, padding: "14px 16px", marginTop: 12 }}>
      <div style={{ display: "flex", gap: 14, alignItems: "flex-end", flexWrap: "wrap" }}>
        {fields.map((f) => (
          <label key={f.key} style={{ flex: `1 1 ${f.width || 240}px`, minWidth: 0 }}>
            <span style={{ display: "block", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: B.label, fontWeight: 700, marginBottom: 7 }}>{f.label}</span>
            {f.options ? (
              /* A choice saves as it is made: there is no "way out" of a select
                 to wait for. `null` for the empty option, because the columns
                 behind these are foreign keys and "" is not a key. */
              <select value={draft?.[f.key] || ""} aria-label={f.label}
                onChange={(e) => {
                  const v = e.target.value || null;
                  setDraft((d) => ({ ...d, [f.key]: v }));
                  if ((record?.[f.key] || null) !== v) onSave?.({ ...record, ...draft, [f.key]: v });
                }}
                style={{ ...input, fontSize: 13.5 }}>
                {f.options.map(([value, text]) => <option key={value} value={value}>{text}</option>)}
              </select>
            ) : (
              <input value={draft?.[f.key] || ""} placeholder={f.placeholder} aria-label={f.label}
                onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                onBlur={() => commit(f.key)}
                style={{ ...input, fontSize: 13.5 }} />
            )}
            {f.hint && <span style={{ display: "block", fontSize: 11.5, color: B.dim, lineHeight: 1.5, marginTop: 6 }}>{f.hint}</span>}
          </label>
        ))}
      </div>
      {onDelete && (
        <div style={{ display: "flex", marginTop: 14 }}>
          <button onClick={onDelete} style={{ ...btnGhost, marginLeft: "auto", padding: "8px 14px", fontSize: 12.5, color: B.salmon, borderColor: B.salmonLine, display: "flex", alignItems: "center", gap: 6 }}>
            <Trash2 size={13} /> {deleteLabel || `Delete this ${noun}`}
          </button>
        </div>
      )}
    </div>
  );
}


/* ── The Level 1 switcher ───────────────────────────────────────────────────
   Which site's pages, which course's modules. It lives in the bar rather than
   on the copy screen alone (the brief put it there) because the Table is the
   landing screen: pages from a site she cannot see the name of is the worst
   place to be (spec 2.2).

   Closes on a transparent sheet rather than a document listener, which is what
   every other menu in here does. */
export function L1Switcher({
  items = [], selectedId, onSelect, onAdd, addOptions = [], noun = "site", loading = false,
  labelFor = (r) => r?.name || r?.title,
}) {
  const [open, setOpen] = useState(false);
  const current = items.find((r) => r.id === selectedId) || items[0] || null;

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button onClick={() => setOpen((v) => !v)} aria-haspopup="listbox" aria-expanded={open}
        aria-label={`Which ${noun}?`}
        style={{ ...btnGhost, padding: "10px 14px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 8, color: B.text }}>
        {/* The name alone, not "Site: My site". The breadcrumb above already
            says which tool she is in, so the label would be saying it twice.

            "No site yet" IS A CLAIM, and it must never be made while the list
            is still arriving. An empty list plus a still-running load is "I do
            not know yet", and saying the other thing is the same mistake as an
            empty table reading as "you have no pages". It showed for real: a
            crash remounted the panel, the store started over, and the bar
            announced she had no site until the query came back. */}
        {current ? labelFor(current) : loading ? `Loading…` : `No ${noun} yet`}
        <ChevronDown size={13} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 150ms ease" }} />
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 19 }} />
          <div role="listbox" style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 20, minWidth: 250, background: B.surface, border: `1px solid ${B.border2}`, borderRadius: 10, padding: 5, boxShadow: "var(--shadow-menu)" }}>
            {items.map((r) => {
              const on = r.id === current?.id;
              return (
                <div key={r.id} role="option" aria-selected={on}
                  onClick={() => { setOpen(false); if (!on) onSelect?.(r); }}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, padding: "9px 10px", borderRadius: 7, cursor: on ? "default" : "pointer", background: on ? B.tealQuiet : "transparent" }}>
                  <span style={{ fontSize: 13, fontWeight: on ? 700 : 600, color: on ? B.teal : B.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {labelFor(r) || `Untitled ${noun}`}
                  </span>
                </div>
              );
            })}
            {/* Adding one lives at the bottom of the same menu — a tool with a
                Level 1 has two Adds, and the alternative is two buttons in the
                bar that differ by one word.

                A KIND is chosen here rather than afterwards: a landing site
                offers different standard pages, so a site made before its kind
                is picked is made against the wrong list. Same call Outreach
                makes about a sequence's platform. One option renders as one
                row, so Courses gets a plain "New course". */}
            {addOptions.length > 0 && (
              <div style={{ marginTop: items.length ? 4 : 0, borderTop: items.length ? `1px solid ${B.border}` : "none", paddingTop: items.length ? 4 : 0 }}>
                {addOptions.map((o) => (
                  <div key={o.id || "add"} onClick={() => { setOpen(false); onAdd?.(o.id); }}
                    style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "9px 10px", borderRadius: 7, cursor: "pointer" }}>
                    <Plus size={13} style={{ color: B.teal, flexShrink: 0, marginTop: 2 }} />
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: "block", color: B.teal, fontSize: 12.5, fontWeight: 700 }}>{o.label}</span>
                      {o.hint && <span style={{ display: "block", color: B.dim, fontSize: 11.5, lineHeight: 1.45, marginTop: 2 }}>{o.hint}</span>}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}


/* ── The rail ───────────────────────────────────────────────────────────────
   The records she moves between while she writes, down the left of the copy
   view. She should never have to go back out to the Table to put one page
   before another (spec 2.2), so this is where reordering lives now — by grip,
   dnd-kit, renumbering the list, per rule 10.

   `rowFor` is what keeps this panel-agnostic: it is handed a record and returns
   what that row READS — a name, a line of meta, a dot and its tooltip, and an
   optional number for a list whose position is part of its meaning (a course's
   modules run in order; a site's pages do not).

   THE DOT IS ON THE RIGHT, in its own column, on all three. Website used to put
   it in front of the name, which meant the same fact sat in two different
   places depending on which tool she was in, and a long page name pushed it
   around. */
export function RecordRail({ records = [], selectedId, onSelect, onReorder, rowFor, noun = "record", onMark }) {
  const twoPane = useTwoPane();

  /* ── MARKING IT DONE FROM THE RAIL ────────────────────────────────────────
     Her read, 2026-09-15: "it's not really obvious — you should also be able to
     click the yellow dot on the sidebar and have it shift to done, and it can
     say done and then fade or something."

     The dot was already the thing she looked at to know where a page stood; it
     just was not the thing she could press. The toggle on the page itself is
     three clicks away when she is scanning a list, and a control you have to
     open a record to reach is a control she will not use while reviewing.

     IT TOGGLES BOTH WAYS. Done is her mark and she has to be able to take it
     back — a one-way press on a row she clicked by accident would be a thing
     she cannot undo without opening the record, which is the trip this exists
     to save.

     THE HOOKS ARE UP HERE, ABOVE THE `!twoPane` EARLY RETURN, AND MUST STAY.
     That return is why `WebsitePage` crashed with React #310 for three weeks:
     a `useState` below a guard runs a different number of hooks depending on a
     prop. src/hookOrder.test.js sweeps for exactly this shape. */
  const [flash, setFlash] = useState(null);
  const timers = useRef([]);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const mark = (record, next) => {
    onMark?.(record, next);
    if (next !== "done") return;          // only the finishing says so out loud
    timers.current.forEach(clearTimeout);
    setFlash({ id: record.id, fading: false });
    timers.current = [
      setTimeout(() => setFlash((f) => (f && f.id === record.id ? { ...f, fading: true } : f)), 700),
      setTimeout(() => setFlash((f) => (f && f.id === record.id ? null : f)), 1400),
    ];
  };

  /* NARROW: A LIST, ON ITS OWN, NOT A STRIP OF CHIPS ABOVE THE RECORD.
     Her report, 2026-09-17, testing on a phone: "the sites list is fine but
     then the pages sidebar isn't as clear in mobile, it's in that slide bar.
     It's even worse in courses... having it as a separate page with a back
     button so you can go to the pages would be best."

     The chips fitted three names and scrolled the rest off the edge, under a
     heading that named the record she was already in — so the one list she
     needed on a phone was the one she could not read. RecordCopy shows this
     INSTEAD of the record now (never both), and the record gets a way back.

     Full rows carry what the wide rail carries: her name for it, the meta line,
     the dot she can press to mark it Done. Still no drag — a 14px grip between
     two thumbs is a drag she would lose, and the Table has it. */
  if (!twoPane) {
    return (
      <div style={{ border: `1px solid ${B.border}`, borderRadius: 12, background: B.surface, overflow: "hidden" }}>
        {records.map((r, i) => {
          const on = r.id === selectedId;
          const row = rowFor(r, i) || {};
          return (
            <div key={r.id} onClick={() => onSelect?.(r)}
              style={{
                display: "flex", alignItems: "center", gap: 10, padding: "14px 14px", cursor: "pointer", minHeight: 56,
                borderBottom: i === records.length - 1 ? "none" : `1px solid ${B.border}`,
                background: on ? B.tealQuiet : B.surface,
              }}>
              {row.number != null && (
                <span style={{ flexShrink: 0, width: 16, fontSize: 13, fontWeight: 700, color: on ? B.teal : B.faint, fontVariantNumeric: "tabular-nums" }}>{row.number}</span>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: on ? 700 : 600, lineHeight: 1.35, color: on ? B.teal : row.title ? B.text : B.ghost }}>
                  {row.title || `Untitled ${noun}`}
                </div>
                {row.meta && <div style={{ fontSize: 11.5, color: B.faint, marginTop: 2 }}>{row.meta}</div>}
              </div>
              {flash?.id === r.id && (
                <span style={{ flexShrink: 0, fontSize: 10.5, fontWeight: 700, color: B.teal, opacity: flash.fading ? 0 : 1, transition: "opacity 650ms ease" }}>Done</span>
              )}
              {row.dot && (row.markable && onMark ? (
                <button onClick={(e) => { e.stopPropagation(); mark(r, row.done ? "draft" : "done"); }}
                  title={row.done ? `Mark this ${noun} as still a draft` : `Mark this ${noun} done`}
                  aria-label={row.done ? `Mark this ${noun} as still a draft` : `Mark this ${noun} done`}
                  style={{ flexShrink: 0, width: 44, height: 44, padding: 0, border: "none", background: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ width: 10, height: 10, borderRadius: "50%", background: row.dot }} />
                </button>
              ) : (
                <span title={row.dotTitle} style={{ flexShrink: 0, width: 10, height: 10, borderRadius: "50%", background: row.dot }} />
              ))}
              <span aria-hidden="true" style={{ flexShrink: 0, color: B.ghost, display: "flex" }}>
                <ChevronRight size={15} />
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div style={{ border: `1px solid ${B.border}`, borderRadius: 12, background: B.surface, overflow: "hidden" }}>
      <SortableList ids={records.map((r) => r.id)} onReorder={(from, to) => onReorder?.(from, to)}>
        {records.map((r, i) => {
          const on = r.id === selectedId;
          const row = rowFor(r, i) || {};
          return (
            <SortableItem key={r.id} id={r.id}>
              {({ handle, dragging }) => (
                <div onClick={() => onSelect?.(r)}
                  style={{
                    display: "flex", alignItems: "center", gap: 8, padding: "10px 11px 10px 5px", cursor: "pointer",
                    borderBottom: i === records.length - 1 ? "none" : `1px solid ${B.border}`,
                    background: on ? B.tealQuiet : B.surface,
                    opacity: dragging ? 0.4 : 1,
                  }}>
                  {/* The grip is a button inside a row that selects on click, so
                      the click has to stop here — otherwise picking a page up to
                      move it also opens it, and she loses her place in the one
                      she was writing. */}
                  <span onClick={(e) => e.stopPropagation()} style={{ display: "flex", flexShrink: 0 }}>{handle}</span>
                  {row.number != null && (
                    <span style={{ flexShrink: 0, width: 15, fontSize: 12.5, fontWeight: 700, color: on ? B.teal : B.faint, fontVariantNumeric: "tabular-nums" }}>{row.number}</span>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: on ? 700 : 600, lineHeight: 1.35, color: on ? B.teal : row.title ? B.text : B.ghost, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {row.title || `Untitled ${noun}`}
                    </div>
                    {row.meta && <div style={{ fontSize: 11, color: B.faint, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.meta}</div>}
                  </div>
                  {/* The word she just earned, then gone. Long enough to see it
                      land, short enough not to become furniture on the row. */}
                  {flash?.id === r.id && (
                    <span style={{ flexShrink: 0, fontSize: 10.5, fontWeight: 700, color: B.teal, opacity: flash.fading ? 0 : 1, transition: "opacity 650ms ease" }}>
                      Done
                    </span>
                  )}
                  {row.dot && (row.markable && onMark ? (
                    /* stopPropagation, or marking a page also opens it and she
                        loses her place in the one she was writing — the same
                        rule the grip follows two lines up. */
                    <button onClick={(e) => { e.stopPropagation(); mark(r, row.done ? "draft" : "done"); }}
                      title={row.done ? `Mark this ${noun} as still a draft` : `Mark this ${noun} done`}
                      aria-label={row.done ? `Mark this ${noun} as still a draft` : `Mark this ${noun} done`}
                      style={{ flexShrink: 0, width: 16, height: 16, padding: 0, border: "none", background: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: row.dot }} />
                    </button>
                  ) : (
                    <span title={row.dotTitle} style={{ flexShrink: 0, width: 8, height: 8, borderRadius: "50%", background: row.dot }} />
                  ))}
                </div>
              )}
            </SortableItem>
          );
        })}
      </SortableList>
    </div>
  );
}


/* ── The copy view ──────────────────────────────────────────────────────────
   The rail and the record she is in, side by side. One width for every tool:
   what she learns about where things are on Sites has to still be true on
   Courses.

   `bare` is the drafting screen. Writing one block with Brill is its own
   two-column layout, so the rail steps aside rather than becoming a third
   column of things she is not doing. All three panels already did this; it is
   one rule here instead of three copies of it. */
/**
 * Which of a phone's two screens a record panel is showing.
 *
 * RecordCopy keeps this itself and nothing else usually cares. The exception is
 * a control rendered OUTSIDE it that has to mean the same thing — the page
 * footer's way back, which sits inside `children` and so cannot see it. Hold
 * this where both can reach it (the hub) and hand it to both.
 */
export function useRecordScreen() {
  const [listOpen, setListOpen] = useState(true);
  return { listOpen, setListOpen };
}

export function RecordCopy({
  records = [], selectedId, onSelect, onReorder, rowFor, noun = "record", nounPlural, bare, emptyHint, onMark, children,
  /* HOW MANY LEVELS THIS PANEL HAS, and the only thing that differs on a phone.
     Her read, 2026-09-18: "because we did this in sites and courses it goes to
     another list … but that's because offers doesn't have another level. sites
     goes level 1 sites, level 2 pages, level three sections. but offers just
     has two levels."

     THREE (Sites, Courses, Presentations): the rail is a level BELOW the
     Table — pages inside a site, modules inside a course, slides inside a
     deck. That list is new information and earns its own screen.

     TWO (Offers, Scripts, Newsletters, Outreach): the rail and the Table are
     two renderings of ONE set. On a phone that meant being shown the same
     offers twice before being allowed to open one. So there is no list screen:
     the Table IS the list, and `onExit` is the way back to it.

     Three by default, because that is what every caller did before this and a
     panel that has not been told is better off with an extra screen than with
     a way back that goes nowhere. */
  levels = 3, onExit, exitLabel,
  /* From `useRecordScreen`, when a caller needs to read or move this too. */
  screen,
}) {
  const twoPane = useTwoPane();
  const plural = nounPlural || `${noun}s`;

  /* ON A PHONE THESE ARE TWO SCREENS (2026-09-17). The list, or the record she
     picked from it — never both, and never the record with the list squeezed
     into a scrolling strip above it. `listOpen` is the way back; picking a row
     closes it. Both live here rather than in each panel, so Sites, Courses,
     Outreach, Scripts, Presentations and Offers all behave the same on a phone
     the day this lands. */
  /* STARTS ON THE LIST (her report, 2026-09-17: "when you click on the site it
     goes right into the sections... it should first land on the page list and
     then you can click into the sections"). Every panel opens a record with a
     child already selected — the first page, the first module — which is right
     on a wide screen, where the rail is beside it either way, and wrong on a
     phone, where it skips the list entirely.

     CONTROLLED when a `screen` is handed in, for the one case where something
     rendered OUTSIDE this component has to mean the same thing — the page
     footer's way back, which sits inside `children` and cannot see in here.
     The hook runs either way: a prop must never change how many hooks this
     calls (src/hookOrder.test.js). */
  const ownScreen = useState(true);
  const [listOpen, setListOpen] = screen ? [screen.listOpen, screen.setListOpen] : ownScreen;

  /* SWITCHING RECORD PUTS HER BACK ON THE LIST. Changing site from the bar
     keeps this component mounted, so without this she would land inside
     whichever page of the new site got auto-selected — the same jump, one level
     over. Adding a page does NOT trigger it: the selection is still in the set,
     so the new page opens as it should. */
  const ids = records.map((r) => r.id).join(",");
  useEffect(() => {
    if (selectedId && !records.some((r) => r.id === selectedId)) setListOpen(true);
    // `ids` alone on purpose: this fires when the SET changes, not when the
    // selection moves within it.
  }, [ids]);

  if (bare) return children;

  if (records.length === 0) {
    return (
      <div style={{ border: `1px dashed ${B.border2}`, borderRadius: 10, padding: 30, textAlign: "center", color: B.dim, fontSize: 13 }}>
        {/* WHERE ADD ACTUALLY IS, which is no longer the same on every panel.
            It used to say "switch to Table and add one", true when the Table was
            the list of these very records. On Sites and Courses the Table is the
            level ABOVE now (rule 9b) and Add page / Add module is in the bar
            directly overhead — so that sentence would send her up a level to
            look for a button that is on the screen she is already on. Outreach
            still has it on its Table, and still says so. */}
        {emptyHint || <>No {plural} yet. Switch to <b style={nb}>Table</b> and add one.</>}
      </div>
    );
  }

  if (!twoPane) {
    /* TWO LEVELS: no list screen at all. The Table she came from was the list.
       Falls through to the three-level behaviour when the panel has not given
       a way out — an extra screen is survivable, a record with no way back is
       not. */
    if (levels === 2 && onExit) {
      return (
        <div style={{ minWidth: 0 }}>
          <button onClick={onExit}
            style={{ ...btnGhost, padding: "9px 14px", fontSize: 12.5, marginBottom: 14 }}>
            ← {exitLabel || `All ${plural}`}
          </button>
          {children}
        </div>
      );
    }

    /* The list: on purpose when she pressed the way back, and by default when
       nothing is open yet. */
    if (listOpen || !selectedId) {
      return (
        <div style={{ minWidth: 0 }}>
          {/* THE WAY OUT OF THIS LEVEL, on the screen that IS this level.
              Three levels on a phone are three screens, and rule 9c wants one
              way back on each, going up exactly one: All sites here, All pages
              from a page, Back to the page while drafting. Before this the
              panel's bar carried "All sites" on BOTH the list and the record,
              so the record screen showed two backs stacked, pointing at
              different levels. Nothing is removed by this — the pages list
              keeps its screen; All sites is one tap further, through the level
              she passes anyway. */}
          {onExit && (
            <button onClick={onExit}
              style={{ ...btnGhost, padding: "9px 14px", fontSize: 12.5, marginBottom: 14 }}>
              ← {exitLabel || "Back"}
            </button>
          )}
          <RecordRail records={records} selectedId={selectedId} noun={noun} rowFor={rowFor} onMark={onMark}
            onSelect={(r) => { setListOpen(false); onSelect?.(r); }} />
        </div>
      );
    }
    return (
      <div style={{ minWidth: 0 }}>
        {/* The way back, in the same slot as everywhere else: above the record,
            reading what it goes back TO (rule 9c). */}
        <button onClick={() => setListOpen(true)}
          style={{ ...btnGhost, padding: "9px 14px", fontSize: 12.5, marginBottom: 14 }}>
          ← All {plural}
        </button>
        {children}
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "250px 1fr", gap: 26, alignItems: "start" }}>
      <RecordRail records={records} selectedId={selectedId} onSelect={onSelect}
        onReorder={onReorder} rowFor={rowFor} noun={noun} onMark={onMark} />
      <div style={{ minWidth: 0 }}>{children}</div>
    </div>
  );
}


/* ── The footer ─────────────────────────────────────────────────────────────
   `[← All pages] [✓ Saved] … [Delete]`, rule 1. There is nothing to press: the
   fields above have always saved on the way out, so this row REPORTS that
   rather than offering a button that promises it. The read-out goes amber and
   names the reason when something is blocking the save, because "Saved" and
   silence look identical and she would find out later.

   `children` is the middle slot — Course puts its Done toggle there, which is
   the one thing on that screen she sets and nothing sets for her. */
export function RecordFooter({ backLabel, onBack, saving, problem, onDelete, deleteLabel = "Delete", tick = true, children }) {
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginTop: 22 }}>
      {/* BOTH OPTIONAL NOW, and the footer is allowed to be just a Delete. Her
          words, 2026-09-15: "you can take away that whole bar down there,
          except delete, because we moved the back button anyway." A second way
          back at the bottom of a long module is a control she scrolls past to
          reach something already at the top.

          `tick` is opt-out rather than removal. Rule 1 turns on the read-out
          existing SOMEWHERE — "Saved" and silence look identical, so a screen
          that drops it tells her nothing when a save fails. Course moves it up
          beside Draft/Done; it does not lose it. */}
      {onBack && <button style={btnGhost} onClick={onBack}>← {backLabel}</button>}
      {tick && <SavedTick saving={saving} problem={problem} />}
      {children}
      {onDelete && (
        <button onClick={onDelete} style={{ ...btnGhost, marginLeft: "auto", padding: "9px 15px", fontSize: 12.5, color: B.salmon, borderColor: B.salmonLine, display: "flex", alignItems: "center", gap: 6 }}>
          <Trash2 size={14} /> {deleteLabel}
        </button>
      )}
    </div>
  );
}


/* ── The drafting screen's split ────────────────────────────────────────────
   Her design, 2026-09-15. One number — how much of the width the CHAT gets —
   and three ways to set it: three presets, a drag bar, and the breakpoint.
   ONE `ratio`, not a mode enum plus a width: two mechanisms for one thing is
   how they drift, and it is why the presets and the drag never disagree here.

     0.5    half — the most fields on screen at once
     0.667  two thirds — the default. Hers: the conversation is where the work
            happens, and it was 45/55 the other way
     1      full — the chat has the width. It MIRRORS THE ACCELERATOR, which is
            the argument for it: a long conversation lands in the shape of every
            module chat she already works in, rather than a cramped column

   At 1 there is only one pane on screen, so that is the only mode with a
   Chat / Preview toggle. Below the two-pane breakpoint the ratio is ignored and
   it is always 1 — a phone is not a special layout, it is this one with the
   choice taken away. */
export const SPLIT_HALF = 0.5;
export const SPLIT_TWO_THIRDS = 0.667;
export const SPLIT_FULL = 1;

/* Anything narrower than this on either side and the fields stop being usable,
   so the drag stops there. Closing a pane is the control's job — it is the only
   one that knows how to give it back. */
const SPLIT_MIN = 0.35;
const SPLIT_MAX = 0.8;

const SPLIT_KEY = "brill.draftSplit";

/**
 * How much width the chat gets, remembered.
 *
 * BROWSER STORAGE, DELIBERATELY, and not Supabase: this is a fact about how she
 * likes to work at THIS screen size, not about her business. Syncing it to a
 * different-shaped screen would be actively wrong. Every read and write is
 * guarded — a private window or blocked site data throws on access, and the
 * screen has to open anyway.
 */
export function useSplitRatio() {
  const [ratio, set] = useState(() => {
    try {
      const v = parseFloat(window.localStorage.getItem(SPLIT_KEY));
      return Number.isFinite(v) && v >= SPLIT_MIN && v <= SPLIT_FULL ? v : SPLIT_TWO_THIRDS;
    } catch { return SPLIT_TWO_THIRDS; }
  });
  const setRatio = useCallback((v) => {
    const next = v >= SPLIT_FULL ? SPLIT_FULL : Math.min(SPLIT_MAX, Math.max(SPLIT_MIN, v));
    set(next);
    try { window.localStorage.setItem(SPLIT_KEY, String(next)); } catch { /* not worth telling her */ }
  }, []);
  return [ratio, setRatio];
}

/* Which preset a ratio counts as. A dragged ratio lights the nearest one rather
   than lighting none — a control with nothing selected reads as broken. */
export const splitPreset = (ratio) =>
  ratio >= SPLIT_FULL ? "full"
    : Math.abs(ratio - SPLIT_HALF) < Math.abs(ratio - SPLIT_TWO_THIRDS) ? "half"
      : "twoThirds";

/* Half, two thirds, full. The icons carry the difference — a line down the
   middle, a line at two thirds, no line — so it needs no label. It renders in
   the SAME PLACE in every mode, including on the fields pane: without that,
   full is a room with no visible door back. */
export function SplitModeControl({ ratio, onChange }) {
  const at = splitPreset(ratio);
  const seg = (id, value, title, extra) => {
    const on = at === id;
    return (
      <button onClick={() => onChange(value)} title={title} aria-label={title} aria-pressed={on}
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 26, height: 22, padding: 0, borderRadius: 7, border: "none", cursor: "pointer",
          background: on ? B.teal : "transparent", color: on ? "var(--btn-text)" : B.faint,
        }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect width="18" height="16" x="3" y="4" rx="2" />
          {extra}
        </svg>
      </button>
    );
  };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 2, padding: 2, border: `1px solid ${B.border2}`, borderRadius: 9, flexShrink: 0, background: B.surface }}>
      {seg("half", SPLIT_HALF, "Half and half", <path d="M12 4v16" />)}
      {seg("twoThirds", SPLIT_TWO_THIRDS, "Two thirds chat", <path d="M16 4v16" />)}
      {seg("full", SPLIT_FULL, "Full width chat", null)}
    </div>
  );
}

/* Chat or the fields, when there is only room for one. Only ever rendered at
   full width — in a split both are already on screen and there is nothing to
   toggle. */
export function PaneToggle({ pane, setPane, otherLabel = "Preview", icons = false }) {
  const seg = (on) => ({
    padding: icons ? "6px 10px" : "8px 22px", borderRadius: 20, fontSize: 13, fontWeight: on ? 700 : 600,
    cursor: "pointer", fontFamily: "inherit", border: "none", display: "inline-flex", alignItems: "center",
    background: on ? B.surface : "transparent", color: on ? B.text : B.dim,
    boxShadow: on ? "0 1px 3px rgba(0,0,0,0.10)" : "none",
  });
  /* ICONS ON A PHONE — her call, 2026-09-22: "chat can be a bubble and preview
     an eye if we need to, just need to figure out how to have space to actually
     type." Two words cost about 90px of a 375px row that also has to carry the
     way back, where she is, and which scope she is writing in. The label stays
     as the accessible name, so nothing is lost to a screen reader or a tooltip. */
  return (
    <div style={{ display: "inline-flex", gap: 2, padding: 3, borderRadius: 22, background: B.surface2, border: `1px solid ${B.border}`, flexShrink: 0 }}>
      <button style={seg(pane === "chat")} onClick={() => setPane("chat")} aria-label="Chat" title="Chat">
        {icons ? <MessageSquare size={14} /> : "Chat"}
      </button>
      <button style={seg(pane !== "chat")} onClick={() => setPane("fields")} aria-label={otherLabel} title={otherLabel}>
        {icons ? <Eye size={14} /> : otherLabel}
      </button>
    </div>
  );
}

/* The divider. Dragging it writes the same `ratio` the presets write, so there
   is one answer to "how wide is the chat" however she set it.

   Pointer events with capture rather than mouse events on window: capture keeps
   the drag alive over the iframe-free but input-heavy right pane, and it is the
   one API that works the same for touch. */
export function SplitDragBar({ containerRef, onRatio }) {
  const [dragging, setDragging] = useState(false);
  const move = (e) => {
    const el = containerRef.current;
    if (!el) return;
    const box = el.getBoundingClientRect();
    if (box.width <= 0) return;
    onRatio((e.clientX - box.left) / box.width);
  };
  return (
    <div
      role="separator" aria-label="Drag to resize"
      onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); setDragging(true); }}
      onPointerMove={(e) => { if (dragging) move(e); }}
      onPointerUp={(e) => { e.currentTarget.releasePointerCapture(e.pointerId); setDragging(false); }}
      style={{ display: "flex", alignItems: "center", justifyContent: "center", cursor: "col-resize", touchAction: "none" }}
    >
      <div style={{ width: 5, height: 54, borderRadius: 3, background: dragging ? B.teal : B.border2 }} />
    </div>
  );
}
